import { Component, DestroyRef, effect, inject, input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { ToggleSwitch } from 'primeng/toggleswitch';

import { UserStore } from '../../../../core/services/user-store.service';
import { DescripcionNota } from '../../../../shared/interfaces/descripcion.interface';
import { AgregarPersonaAntecedentesIglesia } from '../../agregar-persona-form.interface';
import { buildAntecedentesDescripcionNota } from '../../helpers/antecedentes-nota.helper';

@Component({
  selector: 'app-agregar-persona-paso-informacion-adicional',
  standalone: true,
  imports: [ReactiveFormsModule, Button, InputText, ToggleSwitch],
  templateUrl: './paso-informacion-adicional.component.html',
  styleUrl: './paso-informacion-adicional.component.scss',
})
export class AgregarPersonaPasoInformacionAdicionalComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userStore = inject(UserStore);

  readonly initial = input<AgregarPersonaAntecedentesIglesia | null>(null);

  readonly back = output<void>();
  readonly next = output<DescripcionNota>();
  readonly antecedentes = output<AgregarPersonaAntecedentesIglesia>();

  readonly form = this.fb.nonNullable.group({
    ibaIglesia: [false],
    iglesiaAnterior: [''],
    tiempoIglesia: [''],
    aceptoJesus: [false],
    bautizado: [false],
  });

  private readonly _applyInitial = effect(() => {
    const data = this.initial();
    if (!data) {
      return;
    }
    this.form.patchValue(data);
    this.syncIglesiaFields(data.ibaIglesia);
  });

  ngOnInit(): void {
    this.syncIglesiaFields(this.form.controls.ibaIglesia.value);

    this.form.controls.ibaIglesia.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ibaIglesia) => this.syncIglesiaFields(ibaIglesia));
  }

  showFieldError(field: 'iglesiaAnterior' | 'tiempoIglesia'): boolean {
    const control = this.form.controls[field];
    return control.invalid && control.touched;
  }

  onSubmit(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const data = this.form.getRawValue() as AgregarPersonaAntecedentesIglesia;
    const autor = this.userStore.email() || 'sistema';
    const nota = buildAntecedentesDescripcionNota(data, autor);

    this.antecedentes.emit(data);
    this.next.emit(nota);
  }

  private syncIglesiaFields(ibaIglesia: boolean): void {
    const iglesiaControl = this.form.controls.iglesiaAnterior;
    const tiempoControl = this.form.controls.tiempoIglesia;
    const aceptoJesusControl = this.form.controls.aceptoJesus;

    if (ibaIglesia) {
      iglesiaControl.setValidators([Validators.required]);
      tiempoControl.setValidators([Validators.required]);
    } else {
      iglesiaControl.clearValidators();
      tiempoControl.clearValidators();
      iglesiaControl.setValue('');
      tiempoControl.setValue('');
      aceptoJesusControl.setValue(false);
    }

    iglesiaControl.updateValueAndValidity({ emitEvent: false });
    tiempoControl.updateValueAndValidity({ emitEvent: false });
  }
}
